"use client";

import { Popover, Transition } from "@headlessui/react";
import { FC, Fragment, useEffect, useRef } from "react";
import Avatar from "@/shared/Avatar";
import { ShoppingCartIcon } from "@heroicons/react/24/outline";
import Link from 'next/link';
import useClickOutside from '@/hooks/useClickOutside';
import { useAppSelector, useAppDispatch } from '@/app/lib/hooks';
import { getCart } from '@/app/lib/reducers/cartReducer';
import { useTranslation } from "react-i18next";

interface Props {
  className?: string;
}

const CartDropdown: FC<Props> = ({ className = "" }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("guydeez");
  const cartItems = useAppSelector(state => state.cart.items);
  const cartItemCount = cartItems.length;
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectedLanguage = useAppSelector(state => state.language.selectedLanguage);
  const connectedUser = useAppSelector(state => state.auth.user);
  const imageSrc = "https://guydeez-cms.s3.eu-west-3.amazonaws.com/default.png"; // Default image

  // Fire `getCart` at most once per (user, language) tuple. The cart
  // endpoint returns ~757 KB and takes seconds on the backend — we cannot
  // afford the auth slice rippling a new user-object reference or i18n
  // hydrating `selectedLanguage` from null → "fr_FR" both triggering
  // independent calls.
  const userUuid = connectedUser?.uuid;
  const lastFetchKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!userUuid || !selectedLanguage) return;
    const key = `${userUuid}::${selectedLanguage}`;
    if (lastFetchKeyRef.current === key) return;
    lastFetchKeyRef.current = key;
    dispatch(getCart(selectedLanguage));
  }, [dispatch, selectedLanguage, userUuid]);

  const handleClose = () => {
    const button = dropdownRef.current?.querySelector('button[data-headlessui-state="open"]') as HTMLElement;
    button?.click();
  };

  const getCityName = (item: any) => {
    const rawCityName = item?.tour?.city?.name
      ? item.tour.city.name
      : item?.tour?.city?.slug
        ? item.tour.city.slug
        : '';
    return rawCityName ? rawCityName.charAt(0).toUpperCase() + rawCityName.slice(1) : '';
  };

  // Hook to close the dropdown when clicking outside of it
  useClickOutside(dropdownRef, handleClose);

  return (
    <div ref={dropdownRef} className={`relative flex ${className}`}>
      <Popover style={{display:'contents'}}>
        {({ open, close }) => (
          <>
            <Popover.Button
              aria-label={t('cart', { defaultValue: 'Cart' }) as string}
              className={` ${
                open ? "" : "text-opacity-90"
              } group self-center w-10 h-10 sm:w-12 sm:h-12 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-full inline-flex items-center justify-center text-base font-medium hover:text-opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 relative`}
            >
              {cartItemCount > 0 && (
                <span className="absolute top-2 right-2 w-4 h-4 text-xs rounded-full bg-blue-500 text-white flex items-center justify-center">
                  {cartItemCount}
                </span>
              )}
              <ShoppingCartIcon className="h-6 w-6" aria-hidden="true" />
            </Popover.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel className="absolute z-10 w-screen max-w-xs sm:max-w-sm px-4 top-full -right-28 sm:right-0 sm:px-0">
                <div className="overflow-hidden rounded-2xl shadow-lg ring-1 ring-black ring-opacity-5">
                  <div className="relative grid gap-8 bg-white dark:bg-neutral-800 p-7">
                    <h3 className="text-xl font-semibold">{t('cart_dropdown_cart')}</h3>
                    {cartItems.length > 0 ? (
                      <>
                        {cartItems.map((item, index) => {
                          const cityName = getCityName(item);
                          return (
                          <div
                            key={index}
                            className="flex p-2 pr-8 -m-3 transition duration-150 ease-in-out rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus-visible:ring focus-visible:ring-orange-500 focus-visible:ring-opacity-50 relative"
                          >
                            <Avatar
                              imgUrl={item?.tour?.entry?.galleries?.[0]?.path || imageSrc} // Fallback avatar image
                              sizeClass="w-8 h-8 sm:w-12 sm:h-12 w-12 h-12"
                              width={48}
                              height={48}
                            />

                            <div className="ml-3 sm:ml-4 space-y-1">
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-200">
                                {cityName ? <span className="font-semibold">{cityName}: </span> : null}
                                {item.tour?.name}
                              </p>
                              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                                {t('cart_dropdown_date', {date :item.datetime?.date})}  {t('cart_dropdown_time', {time :item.datetime?.time})}
                              </p>
                              <p className="text-xs text-gray-400 dark:text-gray-400">
                                {t('cart_dropdown_email', {email :item.email})}
                              </p>
                            </div>
                            <span className="absolute right-1 top-1/2 transform -translate-y-1/2 w-2 h-2 rounded-full bg-blue-500"></span>
                          </div>
                          );
                        })}
                        <div className="mt-4">
                          <Link href="/checkout" onClick={close} className="block w-full text-center py-2 px-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition">
                            {t('cart_dropdown_proceed_checkout')}
                          </Link>
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400">{t('cart_dropdown_cart_empty')}</p>
                    )}
                  </div>
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    </div>
  );
};

export default CartDropdown;
